/**
 * Stripe Connect account statuses that represent an active connection
 * - onboarding: Initial setup in progress, user has started connecting
 * - pending: Under verification, can receive contributions
 * - readyToUse: Fully verified, can receive contributions and withdraw
 *
 * Excludes 'rejected' which requires re-connecting/re-onboarding
 */
export type ActiveStripeAccountStatus = 'onboarding' | 'pending' | 'readyToUse';

/**
 * Set of active Stripe account statuses for efficient membership checking
 */
const ACTIVE_STRIPE_ACCOUNT_STATUSES = new Set<ActiveStripeAccountStatus>(['onboarding', 'pending', 'readyToUse']);

/**
 * Check if Stripe Connect account is in an active state (not rejected)
 * Active accounts have an ongoing or completed connection and can be used
 *
 * Use this when determining if user should:
 * - Skip address validation (already went through onboarding)
 * - Hide "Connect Bank Account" button (already connected)
 * - Proceed with their existing Stripe account
 *
 * Rejected accounts require restarting the connection flow
 */
export const hasActiveStripeAccount = (status: string): status is ActiveStripeAccountStatus => {
  return ACTIVE_STRIPE_ACCOUNT_STATUSES.has(status as ActiveStripeAccountStatus);
};

/**
 * Check if account can withdraw funds to bank account
 *
 * Returns true for:
 * - readyToUse: Fully verified, Stripe allows withdrawals
 *
 * Returns false for:
 * - onboarding: Setup incomplete
 * - pending: Verification in progress, Stripe blocks withdrawals
 * - rejected: Account failed verification
 */
export const canWithdrawFunds = (status: string): boolean => {
  return status === 'readyToUse';
};
